import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Button, TextField, Card, CardContent,
    Alert, CircularProgress, Divider
} from '@mui/material';
import { getBrandingForDisplay, updateBranding, resetBranding, uploadBrandingLogo, uploadBrandingLogoMini, uploadBrandingFavicon } from 'services/company-service';
import { useAuthTenant } from 'store/auth';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const ELIGIBLE_PLANS = ['premium'];

export const BrandingSettings = () => {
    const { t } = useTranslation(['company']);
    const queryClient = useQueryClient();
    const tenant = useSelector(useAuthTenant);
    const isEligible = ELIGIBLE_PLANS.includes(tenant?.signedPlan?.toLowerCase() ?? '');
    // Always pass slug to write endpoints — backend resolves target company by role
    const targetSlug = tenant?.slug || null;

    const [primaryColor, setPrimaryColor] = useState('');
    const [secondaryColor, setSecondaryColor] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoMiniFile, setLogoMiniFile] = useState(null);
    const [faviconFile, setFaviconFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoMiniPreview, setLogoMiniPreview] = useState(null);
    const [faviconPreview, setFaviconPreview] = useState(null);
    const logoInputRef = useRef();
    const logoMiniInputRef = useRef();
    const faviconInputRef = useRef();

    const { isLoading, data: brandingData } = useQuery(['company-branding', tenant?.slug], getBrandingForDisplay, {
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        const b = brandingData?.data?.branding;
        if (b === undefined) return;
        setPrimaryColor(b?.primaryColor || '');
        setSecondaryColor(b?.secondaryColor || '');
        setLogoPreview(b?.logoUrl || null);
        setLogoMiniPreview(b?.logoMiniUrl || null);
        setFaviconPreview(b?.faviconUrl || null);
    }, [brandingData]);

    const saveMutation = useMutation(
        async () => {
            if (logoFile) await uploadBrandingLogo(logoFile, targetSlug);
            if (logoMiniFile) await uploadBrandingLogoMini(logoMiniFile, targetSlug);
            if (faviconFile) await uploadBrandingFavicon(faviconFile, targetSlug);
            await updateBranding({ primaryColor, secondaryColor }, targetSlug);
        },
        {
            onSuccess: () => {
                // Aplica as cores imediatamente no contexto atual
                const root = document.documentElement;
                if (primaryColor) root.style.setProperty('--color-primary', primaryColor);
                else root.style.removeProperty('--color-primary');
                if (secondaryColor) root.style.setProperty('--color-secondary', secondaryColor);
                else root.style.removeProperty('--color-secondary');

                setLogoFile(null);
                setLogoMiniFile(null);
                setFaviconFile(null);
                queryClient.invalidateQueries(['company-branding', tenant?.slug]);
                Swal.fire({ icon: 'success', title: t('company:branding.messages.saveSuccess'), timer: 2000, showConfirmButton: false });
            },
            onError: () => {
                Swal.fire({ icon: 'error', title: t('company:branding.messages.saveError'), text: t('company:branding.messages.saveErrorDetail') });
            }
        }
    );

    const resetMutation = useMutation(
        () => resetBranding(targetSlug),
        {
            onSuccess: () => {
                setPrimaryColor('');
                setSecondaryColor('');
                setLogoFile(null);
                setLogoMiniFile(null);
                setFaviconFile(null);
                setLogoPreview(null);
                setLogoMiniPreview(null);
                setFaviconPreview(null);
                const root = document.documentElement;
                root.style.removeProperty('--color-primary');
                root.style.removeProperty('--color-secondary');
                queryClient.invalidateQueries(['company-branding', tenant?.slug]);
                Swal.fire({ icon: 'success', title: t('company:branding.messages.resetSuccess'), timer: 2000, showConfirmButton: false });
            }
        }
    );

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleLogoMiniChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoMiniFile(file);
        setLogoMiniPreview(URL.createObjectURL(file));
    };

    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFaviconFile(file);
        setFaviconPreview(URL.createObjectURL(file));
    };

    if (!isEligible) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>{t('company:branding.title')}</Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('company:branding.eligibility.message')}<strong>{t('company:branding.eligibility.planName')}</strong>.
                    <br />{t('company:branding.eligibility.upgradePrompt')}
                </Alert>
            </Box>
        );
    }

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: 4, maxWidth: 700 }}>
            <Typography variant="h5" gutterBottom>{t('company:branding.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('company:branding.description')}
            </Typography>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('company:branding.logo.title')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('company:branding.logo.hint')}
                    </Typography>
                    {logoPreview && (
                        <Box sx={{ mb: 2, p: 1, bgcolor: 'var(--color-primary, #0039c5)', display: 'inline-flex', borderRadius: 1 }}>
                            <img src={logoPreview} alt="Logo preview" style={{ maxHeight: 48, maxWidth: 220, objectFit: 'contain' }} />
                        </Box>
                    )}
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={handleLogoChange}
                    />
                    <Box sx={{ mt: logoPreview ? 1 : 0 }}>
                        <Button variant="outlined" size="small" onClick={() => logoInputRef.current.click()}>
                            {logoPreview ? t('company:branding.logo.change') : t('company:branding.logo.upload')}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('company:branding.logoMini.title')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('company:branding.logoMini.hint')}
                    </Typography>
                    {logoMiniPreview && (
                        <Box sx={{ mb: 2, p: 1, bgcolor: 'var(--color-primary, #0039c5)', display: 'inline-flex', borderRadius: 1 }}>
                            <img src={logoMiniPreview} alt="Logo mini preview" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                        </Box>
                    )}
                    <input
                        ref={logoMiniInputRef}
                        type="file"
                        accept="image/png,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={handleLogoMiniChange}
                    />
                    <Box sx={{ mt: logoMiniPreview ? 1 : 0 }}>
                        <Button variant="outlined" size="small" onClick={() => logoMiniInputRef.current.click()}>
                            {logoMiniPreview ? t('company:branding.logoMini.change') : t('company:branding.logoMini.upload')}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('company:branding.favicon.title')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('company:branding.favicon.hint')}
                    </Typography>
                    {faviconPreview && (
                        <Box sx={{ mb: 2 }}>
                            <img src={faviconPreview} alt="Favicon preview" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                        </Box>
                    )}
                    <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/x-icon,image/png,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={handleFaviconChange}
                    />
                    <Button variant="outlined" size="small" onClick={() => faviconInputRef.current.click()}>
                        {faviconPreview ? t('company:branding.favicon.change') : t('company:branding.favicon.upload')}
                    </Button>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('company:branding.colors.title')}</Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                type="color"
                                value={primaryColor || '#1A73E8'}
                                onChange={(e) => setPrimaryColor(e.target.value)}
                                style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', padding: 0 }}
                            />
                            <TextField
                                size="small"
                                label={t('company:branding.colors.primaryLabel')}
                                value={primaryColor}
                                onChange={(e) => setPrimaryColor(e.target.value)}
                                placeholder="#1A73E8"
                                sx={{ width: 140 }}
                                inputProps={{ maxLength: 7 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                type="color"
                                value={secondaryColor || '#F5A623'}
                                onChange={(e) => setSecondaryColor(e.target.value)}
                                style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', padding: 0 }}
                            />
                            <TextField
                                size="small"
                                label={t('company:branding.colors.secondaryLabel')}
                                value={secondaryColor}
                                onChange={(e) => setSecondaryColor(e.target.value)}
                                placeholder="#F5A623"
                                sx={{ width: 140 }}
                                inputProps={{ maxLength: 7 }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isLoading}
                >
                    {saveMutation.isLoading ? <CircularProgress size={20} /> : t('company:branding.actions.save')}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => resetMutation.mutate()}
                    disabled={resetMutation.isLoading}
                >
                    {t('company:branding.actions.reset')}
                </Button>
            </Box>
        </Box>
    );
};

export default BrandingSettings;
